import React, { useState, useMemo, useRef, useEffect } from "react";
import { Button, Form, Dropdown } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  FaSearch,
  FaChevronDown,
  FaFlag,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaTimes,
  FaEllipsisV,
} from "react-icons/fa";
import TablePaginationBar from "../components/TablePaginationBar";
import PatientActionsDropdown, {
  MENU_WIDTH,
  MENU_HEIGHT,
} from "../components/MyPatients/PatientActionsDropdown";
import DeactivatePatientModal from "../components/MyPatients/DeactivatePatientModal";
import { isCoCMSuperAdmin } from "../constants/cocmDeactivate";
import { resolveCoCMPatientDisplayId } from "../utils/cocmPatientId";

const statusColors = {
  Active: { bg: "#D0FAE5", color: "#006145" },
  Intake: { bg: "#E8F1FF", color: "#2F6FED" },
  Monitoring: { bg: "#F2ECFF", color: "#7A52CC" },
  Outreach: { bg: "#FFF4E5", color: "#D9822B" },
  Inactive: { bg: "#FEE2E2", color: "#991B1B" },
};

const formatDateToYYYYMMDD = (dateStr) => {
  if (!dateStr) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  const mdyMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdyMatch) {
    const [, m, d, y] = mdyMatch;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatDateToMMDDYYYY = (dateStr) => {
  if (!dateStr) return "";
  const yyyymmdd = formatDateToYYYYMMDD(dateStr);
  if (!yyyymmdd) return "";
  const [y, m, d] = yyyymmdd.split("-");
  return `${m}/${d}/${y}`;
};

const parseDateToMs = (dateStr) => {
  if (!dateStr) return 0;
  const yyyymmdd = formatDateToYYYYMMDD(dateStr);
  if (!yyyymmdd) return 0;
  return new Date(yyyymmdd).getTime();
};

/* ─── ClickUpDatePicker Component ─── */
const ClickUpDatePicker = ({ value, onChange }) => {
  const [hovered, setHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  const checkIfOverdue = (dateString) => {
    if (!dateString) return false;
    const parts = dateString.split("-");
    if (parts.length !== 3) return false;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);

    const date = new Date(year, month, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isOverdue = checkIfOverdue(value);

  const handleClear = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onChange("");
  };

  const handleContainerClick = (e) => {
    if (e.target.closest(".clear-btn")) return;
    if (inputRef.current) {
      try {
        inputRef.current.showPicker();
      } catch (err) {
        inputRef.current.focus();
      }
    }
  };

  const formattedDate = value ? formatDateToMMDDYYYY(value) : "";

  return (
    <div
      onClick={handleContainerClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "6px 12px",
        borderRadius: "6px",
        backgroundColor: isFocused
          ? "#E6F0FF"
          : value
            ? hovered
              ? "#E6F0FF"
              : "transparent"
            : hovered
              ? "#E6F0FF"
              : "#E9EEF1",
        border: isFocused
          ? "1px solid #2F6FED"
          : value && hovered
            ? "1px solid #B3D4FF"
            : "1px solid transparent",
        cursor: "pointer",
        minHeight: "28px",
        minWidth: "120px",
        transition: "all 0.2s ease",
        userSelect: "none",
      }}
    >
      {/* Hidden standard input */}
      <input
        ref={inputRef}
        type="date"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          opacity: 0,
          cursor: "pointer",
          zIndex: 2,
        }}
      />

      {/* Visual State */}
      {value ? (
        <span
          style={{
            color: isOverdue ? "#D64545" : "#333333",
            fontWeight: "500",
            fontSize: "13px",
            zIndex: 1,
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <span>{formattedDate}</span>
          {hovered && (
            <button
              className="clear-btn"
              onClick={handleClear}
              style={{
                background: "none",
                border: "none",
                padding: "2px",
                color: "#8E8E93",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                zIndex: 3,
                marginLeft: "4px",
              }}
            >
              <FaTimes size={10} />
            </button>
          )}
        </span>
      ) : (
        <span
          style={{
            zIndex: 1,
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            width: "100%",
            justifyContent: "center",
            color: hovered ? "#27B99A" : "#7F8C8D",
          }}
        >
          {hovered ? (
            <FaCalendarAlt size={12} style={{ color: "#27B99A" }} />
          ) : (
            <span
              style={{
                fontSize: "12px",
                color: "#A0AEC0",
                letterSpacing: "0.5px",
              }}
            >
              Set Date
            </span>
          )}
        </span>
      )}
    </div>
  );
};

/* ─── RegistryCoCM Page Component ─── */
const MyPatients = () => {
  const [activeTab, setActiveTab] = useState("Active");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [actionPatient, setActionPatient] = useState(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  // Sorting State
  const [sortColumn, setSortColumn] = useState("mostRecentActivity");
  const [sortDirection, setSortDirection] = useState("desc"); // 'asc', 'desc', or null

  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state?.user?.user);
  const canDeactivatePatients = isCoCMSuperAdmin(user);
  const isCareManagerRegistry = location.pathname.includes("careManager");
  const isPsychiatricConsultant =
    user?.role === "psychiatricConsultant" ||
    user?.providerType === "Psychiatric Consultant";

  /* ─── Data State ─── */
  const [patients, setPatients] = useState([]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("user_id");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/get-patients`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.data?.success) {
        const mapped = response.data.data.map((user) => {
          const act1 = parseDateToMs(user.cocmMeta?.lastFollowUp);
          const act2 = parseDateToMs(user.cocmMeta?.lastCaseReview);
          const act3 = parseDateToMs(user.cocmMeta?.initialAssessment);
          const mostRecentActivity = Math.max(act1, act2, act3);

          return {
            id: user._id,
            patientId: resolveCoCMPatientDisplayId(user),
            name: `${user.firstname} ${user.lastname}`,
            accesscode:
              user.clinicAccessCode ||
              user.cocmMeta?.accessCode?.[0]?.accessCode ||
              user.cocmMeta?.accesscode ||
              "",
            dob: user.dob ? formatDateToMMDDYYYY(user.dob) : "",
            email: user.email,
            phone: user.phoneno,
            status: user.cocmMeta?.status || "Intake",
            clinic:
              user.cocmMeta?.clinic ||
              user.cocmMeta?.accessCode?.[0]?.clinic ||
              "",
            cm: user.cocmMeta?.careManager || "",
            initialAssessment: user.cocmMeta?.initialAssessment
              ? formatDateToMMDDYYYY(user.cocmMeta.initialAssessment)
              : "",
            lastFollowUp: user.cocmMeta?.lastFollowUp
              ? formatDateToMMDDYYYY(user.cocmMeta.lastFollowUp)
              : "",
            nextFollowUp: user.cocmMeta?.nextFollowUp
              ? formatDateToYYYYMMDD(user.cocmMeta.nextFollowUp)
              : "",
            lastCaseReview: user.cocmMeta?.lastCaseReview
              ? formatDateToMMDDYYYY(user.cocmMeta.lastCaseReview)
              : "",
            nextCaseReview: user.cocmMeta?.nextCaseReview
              ? formatDateToYYYYMMDD(user.cocmMeta.nextCaseReview)
              : "",
            phq9: user.cocmMeta?.phq9?.latest ?? 0,
            gad7: user.cocmMeta?.gad7?.latest ?? 0,
            smfq: user.cocmMeta?.smfq?.latest ?? 0,
            scared: user.cocmMeta?.scared?.latest ?? 0,
            minutes: user.cocmMeta?.minutes || 0,
            flags: user.cocmMeta?.flags || { flag: false, warning: false },
            mostRecentActivity: mostRecentActivity || 0,
          };
        });
        setPatients(mapped);
      }
    } catch (error) {
      console.error("Error fetching patients:", error);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  const handleToggleFlag = async (id, field) => {
    const patient = patients.find((p) => p.id === id);
    if (!patient || patient.status === "Inactive") return;

    const updatedFlags = {
      ...patient.flags,
      [field]: !patient.flags[field],
    };

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/users/edit-patient/${id}`,
        {
          flags: updatedFlags,
        },
        { headers: getAuthHeaders() },
      );

      if (response.data?.success) {
        setPatients((prev) =>
          prev.map((p) => {
            if (p.id === id) {
              const updatedPatient = {
                ...p,
                flags: updatedFlags,
              };
              if (selectedPatient?.id === id) {
                setSelectedPatient(updatedPatient);
              }
              return updatedPatient;
            }
            return p;
          }),
        );
      }
    } catch (error) {
      console.error("Error updating flag:", error);
      alert(
        error.response?.data?.message ||
          "Failed to update flag. Please try again.",
      );
    }
  };

  const handleDateChange = async (id, field, newValue) => {
    const patient = patients.find((p) => p.id === id);
    if (!patient || patient.status === "Inactive") return;

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/users/edit-patient/${id}`,
        {
          [field]: newValue,
        },
        { headers: getAuthHeaders() },
      );

      if (response.data?.success) {
        setPatients((prev) =>
          prev.map((p) => {
            if (p.id === id) {
              const updatedPatient = {
                ...p,
                [field]: newValue,
              };
              if (selectedPatient?.id === id) {
                setSelectedPatient(updatedPatient);
              }
              return updatedPatient;
            }
            return p;
          }),
        );
      }
    } catch (error) {
      console.error("Error updating date:", error);
      alert(
        error.response?.data?.message ||
          "Failed to update date. Please try again.",
      );
    }
  };

  /* ─── Sorting Logic ─── */
  const handleSort = (columnId) => {
    if (sortColumn === columnId) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortColumn("mostRecentActivity");
        setSortDirection("desc");
      } else {
        setSortDirection("asc");
      }
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  /* ─── Filter & Sort Data ─── */
  const filteredAndSortedData = useMemo(() => {
    // 1. Filter
    let result = patients.filter((item) => {
      const matchesSearch =
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.patientId.toLowerCase().includes(search.toLowerCase()) ||
        item.clinic.toLowerCase().includes(search.toLowerCase()) ||
        item.cm.toLowerCase().includes(search.toLowerCase());
      const matchesStatus =
        statusFilter === "All" || item.status === statusFilter;
      const matchesTab =
        activeTab === "Active"
          ? item.status !== "Inactive"
          : item.status === "Inactive";
      return matchesSearch && matchesStatus && matchesTab;
    });

    // 2. Sort
    if (sortColumn && sortDirection) {
      result.sort((a, b) => {
        let valA = a[sortColumn];
        let valB = b[sortColumn];

        if (sortColumn === "flags") {
          const scoreA = (a.flags?.flag ? 2 : 0) + (a.flags?.warning ? 4 : 0);
          const scoreB = (b.flags?.flag ? 2 : 0) + (b.flags?.warning ? 4 : 0);
          return sortDirection === "asc" ? scoreA - scoreB : scoreB - scoreA;
        }

        if (
          [
            "dob",
            "initialAssessment",
            "lastFollowUp",
            "nextFollowUp",
            "lastCaseReview",
            "nextCaseReview",
          ].includes(sortColumn)
        ) {
          const dateA = valA
            ? new Date(formatDateToYYYYMMDD(valA))
            : new Date(0);
          const dateB = valB
            ? new Date(formatDateToYYYYMMDD(valB))
            : new Date(0);
          return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
        }

        if (typeof valA === "number" && typeof valB === "number") {
          return sortDirection === "asc" ? valA - valB : valB - valA;
        }

        valA = valA ? valA.toString().toLowerCase() : "";
        valB = valB ? valB.toString().toLowerCase() : "";
        if (valA < valB) return sortDirection === "asc" ? -1 : 1;
        if (valA > valB) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [
    patients,
    search,
    statusFilter,
    activeTab,
    sortColumn,
    sortDirection,
    isCareManagerRegistry,
    isPsychiatricConsultant,
  ]);

  /* ─── Pagination ─── */
  const totalPages = Math.ceil(filteredAndSortedData.length / rowsPerPage);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredAndSortedData.slice(start, start + rowsPerPage);
  }, [filteredAndSortedData, currentPage, rowsPerPage]);

  /* ─── Status Badge ─── */
  const StatusBadge = ({ status }) => {
    const s = statusColors[status] || {
      bg: "rgba(142,142,147,0.1)",
      color: "#8E8E93",
    };
    return (
      <span
        style={{
          padding: "5px 14px",
          borderRadius: "20px",
          fontSize: "12px",
          fontWeight: 600,
          backgroundColor: s.bg,
          color: s.color,
          display: "inline-block",
        }}
      >
        {status}
      </span>
    );
  };

  /* ─── Sortable Column Header Renderer ─── */
  const renderHeaderCell = (columnId, label, minWidth, stickyClass = "") => {
    const isSorted = sortColumn === columnId;

    return (
      <th
        className={stickyClass}
        onClick={() => handleSort(columnId)}
        style={{
          minWidth,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <div
          className={`d-flex align-items-center gap-2 sortable-header-wrapper ${isSorted ? "sorted" : ""}`}
        >
          <span>{label}</span>
          <span
            className="sort-icon-box"
            style={{ display: "inline-flex", alignItems: "center" }}
          >
            {isSorted ? (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "16px",
                  height: "16px",
                  borderRadius: "50%",
                  backgroundColor: "#27B99A",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                  marginLeft: "4px",
                }}
              >
                {sortDirection === "asc" ? (
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="#FFFFFF">
                    <path d="M4 1L7 4.5H5.5V7H2.5V4.5H1L4 1Z" />
                  </svg>
                ) : (
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="#FFFFFF">
                    <path d="M4 7L1 3.5H2.5V1H5.5V3.5H7L4 7Z" />
                  </svg>
                )}
              </div>
            ) : (
              <div
                className="unsorted-arrows"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                  marginLeft: "4px",
                }}
              >
                <svg width="8" height="4" viewBox="0 0 8 4" fill="#CCCCCC">
                  <path d="M4 0L8 4H0L4 0Z" />
                </svg>
                <svg width="8" height="4" viewBox="0 0 8 4" fill="#CCCCCC">
                  <path d="M4 4L0 0L8 0L4 4Z" />
                </svg>
              </div>
            )}
          </span>
        </div>
      </th>
    );
  };

  const toggleDropdown = (id, e) => {
    e.stopPropagation();
    if (openDropdownId === id) {
      setOpenDropdownId(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      let top = rect.bottom + 4;
      let left = rect.right - MENU_WIDTH;

      if (left < 8) left = 8;
      if (left + MENU_WIDTH > window.innerWidth - 8) {
        left = window.innerWidth - MENU_WIDTH - 8;
      }
      if (top + MENU_HEIGHT > window.innerHeight - 8) {
        top = rect.top - MENU_HEIGHT - 4;
      }
      if (top < 8) top = 8;

      setDropdownPos({ top, left });
      setOpenDropdownId(id);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest(".patient-actions-menu") &&
        !event.target.closest(".actions-menu-btn")
      ) {
        setOpenDropdownId(null);
      }
    };
    if (openDropdownId !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdownId]);

  const handleDeactivatePatient = async ({
    inactiveReason,
    inactiveReasonOther,
    keepAppAccess,
  }) => {
    if (!actionPatient) return;

    setDeactivating(true);
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/users/cocm-patient/${actionPatient.id}/deactivate`,
        {
          inactiveReason,
          inactiveReasonOther,
          keepAppAccess,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data?.success) {
        setPatients((prev) =>
          prev.map((p) =>
            p.id === actionPatient.id ? { ...p, status: "Inactive" } : p,
          ),
        );
        setShowDeactivateModal(false);
        setActionPatient(null);
      }
    } catch (error) {
      console.error("Error deactivating patient:", error);
      alert(
        error.response?.data?.message ||
          "Failed to deactivate patient. Please try again.",
      );
    } finally {
      setDeactivating(false);
    }
  };

  const handlePatientAction = (action, patient) => {
    setOpenDropdownId(null);

    switch (action) {
      case "view":
        navigate(`/patient-info/${patient.id}`, {
          state: { returnTo: location.pathname },
        });
        break;
      case "deactivate":
        setActionPatient(patient);
        setShowDeactivateModal(true);
        break;
      default:
        break;
    }
  };

  const closeDeactivateModal = () => {
    if (deactivating) return;
    setShowDeactivateModal(false);
    setActionPatient(null);
  };

  return (
    <div className="app-page cocm-patients-page">
      <style>{`
        .cocm-patients-page { overflow-x: hidden; width: 100%; }
        .custom-dropdown-toggle::after { display: none !important; }

        .sticky-col-flags {
          position: sticky !important;
          left: 0;
          z-index: 5 !important;
          background-color: white !important;
        }
        .flag-icon-btn {
          cursor: pointer;
          transition: transform 0.15s ease, color 0.15s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .flag-icon-btn:hover {
          transform: scale(1.25);
        }
        .sticky-col-id {
          position: sticky !important;
          left: 80px;
          z-index: 5 !important;
          background-color: white !important;
        }
        .sticky-col-name {
          position: sticky !important;
          left: 200px;
          z-index: 5 !important;
          background-color: white !important;
        }

        thead th { position: sticky !important; top: 0; z-index: 10 !important; background-color: #064260 !important; color: white !important; }
        thead th.sticky-col-flags { z-index: 16 !important; background-color: #064260 !important; }
        thead th.sticky-col-id    { z-index: 16 !important; background-color: #064260 !important; }
        thead th.sticky-col-name  { z-index: 16 !important; background-color: #064260 !important; }

        tr:nth-child(even) td.sticky-col-flags,
        tr:nth-child(even) td.sticky-col-id,
        tr:nth-child(even) td.sticky-col-name { background-color: #F8FAFC !important; }

        tr:nth-child(even) td:not(.sticky-col-flags):not(.sticky-col-id):not(.sticky-col-name) {
          background-color: #F8FAFC !important;
        }

        .table-container {
          width: 100%;
          overflow-x: scroll;
          overflow-y: auto;
          max-width: 100%;
          position: relative;
        }
        .custom-table {
          border-collapse: separate;
          border-spacing: 0;
          min-width: 2000px;
        }
        .custom-table th,
        .custom-table td {
          white-space: nowrap !important;
          border-bottom: 1px solid #EAECEF !important;
          border-top: none !important;
          vertical-align: middle !important;
          padding: 14px 16px !important;
        }
        .custom-table td {
          min-height: 64px;
        }
        .custom-table td .patient-name-cell {
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 2px;
          line-height: 1.35;
          white-space: normal;
        }
        .custom-table td .flags-cell {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          min-height: 24px;
        }
        .custom-table td .status-cell {
          display: inline-flex;
          align-items: center;
        }
        .custom-table thead th {
          font-weight: 600 !important;
          font-size: 11px !important;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          color: #ffffff !important;
          padding: 14px 16px !important;
          vertical-align: middle !important;
        }
        .custom-table tbody tr:hover td { background-color: #EBF3FF !important; }

        .pill-tab {
          padding: 8px 24px;
          border-radius: 6px;
          font-weight: 600;
          font-size: 13px;
          border: none;
          transition: all 0.2s;
        }
        .pill-tab.active {
          background-color: #27B99A;
          color: white;
        }
        .pill-tab:not(.active) {
          background-color: #E9EEF1;
          color: #064260;
        }

        /* Sorting Arrows ClickUp Style */
        .sortable-header-wrapper {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .sort-icon-box {
          display: inline-flex;
          align-items: center;
          opacity: 0;
          transition: opacity 0.2s ease-in-out;
        }
        th:hover .sort-icon-box {
          opacity: 0.6;
        }
        .sortable-header-wrapper.sorted .sort-icon-box {
          opacity: 1;
        }
        .actions-menu-btn {
          background-color: transparent;
          border: 1px solid #27B99A;
          color: #27B99A;
          border-radius: 6px;
          font-size: 14px;
          padding: 6px 10px;
          transition: all 0.2s ease;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .actions-menu-btn:hover,
        .actions-menu-btn:focus {
          background-color: #27B99A;
          color: white;
        }
      `}</style>

      {/* ── Tabs ── */}
      <div className="d-flex gap-2" style={{ padding: "0 0 16px 0" }}>
        <button
          className={`pill-tab ${activeTab === "Active" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("Active");
            setCurrentPage(1);
          }}
        >
          Active
        </button>
        <button
          className={`pill-tab ${activeTab === "Inactive" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("Inactive");
            setCurrentPage(1);
          }}
        >
          Inactive
        </button>
      </div>

      <div
        className="bg-white rounded shadow-sm"
        style={{ width: "100%", display: "flex", flexDirection: "column" }}
      >
        {/* ── Header ── */}
        <div className="app-page-toolbar">
          <h2 className="app-page-title">
            My Patients
          </h2>

          <div className="d-flex gap-3 align-items-center flex-wrap">
            {/* Search */}
            <div className="app-filter-search-wrap">
              <FaSearch className="app-filter-search-icon" />
              <Form.Control
                type="text"
                className="app-filter-search"
                placeholder="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                style={{ width: "320px" }}
              />
            </div>

            {/* Status Dropdown */}
            <Dropdown
              onSelect={(val) => {
                setStatusFilter(val);
                setCurrentPage(1);
              }}
            >
              <Dropdown.Toggle
                className="custom-dropdown-toggle app-filter-dropdown"
                style={{ width: "150px" }}
              >
                <span>
                  {statusFilter === "All" ? "Status: All" : statusFilter}
                </span>
                <FaChevronDown
                  size={11}
                  style={{ opacity: 0.7, marginLeft: "6px" }}
                />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item eventKey="All">All</Dropdown.Item>
                <Dropdown.Item eventKey="Intake">Intake</Dropdown.Item>
                <Dropdown.Item eventKey="Active">Active</Dropdown.Item>
                <Dropdown.Item eventKey="Monitoring">Monitoring</Dropdown.Item>
                <Dropdown.Item eventKey="Outreach">Outreach</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="table-container">
          <table className="table custom-table mb-0">
            <thead>
              <tr
                style={{
                  height: "50px",
                  fontSize: "11px",
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                }}
              >
                {renderHeaderCell("flags", "Flags", "80px", "sticky-col-flags")}
                {renderHeaderCell(
                  "patientId",
                  "Patient ID",
                  "120px",
                  "sticky-col-id",
                )}
                {renderHeaderCell("name", "Name", "160px", "sticky-col-name")}
                {renderHeaderCell("dob", "DOB", "100px")}
                {renderHeaderCell("status", "Status", "130px")}
                {(isCareManagerRegistry || isPsychiatricConsultant) &&
                  renderHeaderCell("clinic", "Clinic", "200px")}
                {(isCareManagerRegistry || isPsychiatricConsultant) &&
                  renderHeaderCell("cm", "Care Manager", "160px")}
                {renderHeaderCell("initialAssessment", "I/A", "140px")}
                {renderHeaderCell("lastFollowUp", "Last Follow-Up", "140px")}
                {renderHeaderCell("nextFollowUp", "Next Follow-Up", "160px")}
                {renderHeaderCell(
                  "lastCaseReview",
                  "Last Case Review",
                  "140px",
                )}
                {renderHeaderCell(
                  "nextCaseReview",
                  "Next Case Review",
                  "160px",
                )}
                {renderHeaderCell("phq9", "PHQ-9", "90px")}
                {renderHeaderCell("gad7", "GAD-7", "90px")}
                {renderHeaderCell("smfq", "SMFQ", "90px")}
                {renderHeaderCell("scared", "SCARED", "100px")}
                {renderHeaderCell("minutes", "Minutes this Month", "160px")}
                <th style={{ minWidth: "90px" }}>Actions</th>
              </tr>
            </thead>
            <tbody style={{ fontSize: "13px", color: "#444" }}>
              {paginatedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={
                      isCareManagerRegistry || isPsychiatricConsultant ? 18 : 16
                    }
                    className="text-center py-5 text-muted"
                  >
                    No records found
                  </td>
                </tr>
              ) : (
                paginatedData.map((patient) => (
                  <tr key={patient.id}>
                    {/* Flags */}
                    <td className="sticky-col-flags">
                      <div className="flags-cell" style={{ paddingLeft: "4px" }}>
                        <FaFlag
                          size={14}
                          className={
                            patient.status === "Inactive"
                              ? ""
                              : "flag-icon-btn"
                          }
                          color={patient.flags.flag ? "#F5B041" : "#D1D5DB"}
                          title={
                            patient.status === "Inactive"
                              ? "Flags cannot be updated for inactive patients"
                              : patient.flags.flag
                                ? "Patient flagged for psychiatric consultant review"
                                : "Mark patient for psychiatric case review"
                          }
                          style={{
                            cursor:
                              patient.status === "Inactive"
                                ? "not-allowed"
                                : "pointer",
                            opacity: patient.status === "Inactive" ? 0.5 : 1,
                          }}
                          onClick={
                            patient.status === "Inactive"
                              ? undefined
                              : () => handleToggleFlag(patient.id, "flag")
                          }
                        />
                        <FaExclamationTriangle
                          size={15}
                          className={
                            patient.status === "Inactive"
                              ? ""
                              : "flag-icon-btn"
                          }
                          color={patient.flags.warning ? "#EF4444" : "#D1D5DB"}
                          title={
                            patient.status === "Inactive"
                              ? "Flags cannot be updated for inactive patients"
                              : patient.flags.warning
                                ? "Patient flagged as safety risk"
                                : "Mark patient as safety concern"
                          }
                          style={{
                            cursor:
                              patient.status === "Inactive"
                                ? "not-allowed"
                                : "pointer",
                            opacity: patient.status === "Inactive" ? 0.5 : 1,
                          }}
                          onClick={
                            patient.status === "Inactive"
                              ? undefined
                              : () =>
                                  handleToggleFlag(patient.id, "warning")
                          }
                        />
                      </div>
                    </td>

                    {/* Patient ID */}
                    <td
                      className="sticky-col-id"
                      style={{
                        fontFamily: "monospace",
                        fontSize: "12px",
                        color: "#666",
                      }}
                    >
                      {patient.patientId}
                    </td>

                    {/* Name */}
                    <td
                      className="sticky-col-name fw-semibold"
                      style={{ color: "#064260" }}
                    >
                      <div className="patient-name-cell">
                        <div>{patient.name}</div>
                        {patient.accesscode && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#8E8E93",
                              fontWeight: "normal",
                            }}
                          >
                            Code:{" "}
                            <span style={{ fontFamily: "monospace" }}>
                              {patient.accesscode}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>

                    <td>{patient.dob}</td>

                    {/* Status */}
                    <td>
                      <div className="status-cell">
                        <StatusBadge status={patient.status} />
                      </div>
                    </td>

                    {(isCareManagerRegistry || isPsychiatricConsultant) && (
                      <td>{patient.clinic}</td>
                    )}
                    {(isCareManagerRegistry || isPsychiatricConsultant) && (
                      <td>{patient.cm}</td>
                    )}

                    <td>{patient.initialAssessment}</td>
                    <td>{patient.lastFollowUp}</td>

                    {/* Next Follow-Up (Editable Date) */}
                    <td>
                      {patient.status === "Inactive" ? (
                        patient.nextFollowUp || "—"
                      ) : (
                        <ClickUpDatePicker
                          value={patient.nextFollowUp}
                          onChange={(newVal) =>
                            handleDateChange(patient.id, "nextFollowUp", newVal)
                          }
                        />
                      )}
                    </td>

                    <td>{patient.lastCaseReview}</td>

                    {/* Next Case Review (Editable Date) */}
                    <td>
                      {patient.status === "Inactive" ? (
                        patient.nextCaseReview || "—"
                      ) : (
                        <ClickUpDatePicker
                          value={patient.nextCaseReview}
                          onChange={(newVal) =>
                            handleDateChange(
                              patient.id,
                              "nextCaseReview",
                              newVal,
                            )
                          }
                        />
                      )}
                    </td>

                    {/* Clinical Scores & Minutes */}
                    <td>{patient.phq9}</td>
                    <td>{patient.gad7}</td>
                    <td>{patient.smfq}</td>
                    <td>{patient.scared}</td>
                    <td>{patient.minutes}</td>

                    <td>
                      {isPsychiatricConsultant ? (
                        <Button
                          className="app-view-btn"
                          onClick={() =>
                            navigate(`/patient-info/${patient.id}`)
                          }
                        >
                          View
                        </Button>
                      ) : (
                        <div style={{ display: "inline-block" }}>
                          <button
                            type="button"
                            className="actions-menu-btn"
                            aria-label="Patient actions"
                            aria-expanded={openDropdownId === patient.id}
                            onClick={(e) => toggleDropdown(patient.id, e)}
                          >
                            <FaEllipsisV size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!isPsychiatricConsultant && (
          <PatientActionsDropdown
            openDropdownId={openDropdownId}
            dropdownPos={dropdownPos}
            patients={paginatedData}
            onAction={handlePatientAction}
            canDeactivate={canDeactivatePatients}
          />
        )}

        {showDeactivateModal && actionPatient && (
          <DeactivatePatientModal
            patient={actionPatient}
            onClose={closeDeactivateModal}
            onConfirm={handleDeactivatePatient}
            isSubmitting={deactivating}
          />
        )}

        <TablePaginationBar
          currentPage={currentPage}
          totalItems={filteredAndSortedData.length}
          rowsPerPage={rowsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
          onRowsPerPageChange={(n) => {
            setRowsPerPage(n);
            setCurrentPage(1);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </div>
    </div>
  );
};

export default MyPatients;
